import React from 'react';
import _ from 'lodash';

import {
  Button,
  Paper,
  Stack,
  Typography,
  styled
} from '@mui/material';
import {
  PermissionWall,
  tier1
} from 'modules';

import { modalUtil } from 'iron-forge-react-modules';
import * as dataService from './dataService';



const __variant2BackgroundColor: Record<dataService.IStatus, string> = {
  preAlert: '#FFA8A8',
  itemReceived: '#FFF8E6',
  observationComplete: '#FFF8E6',
  qualityControlComplete: '#FFF8E6',
  triageComplete: '#FFF8E6',
  complete: '#E0FAED'
};

const StyledPaper = styled(Paper, {
  shouldForwardProp: prop => prop !== 'variant'
})<{
  propVariant: dataService.IStatus;
}>(({ propVariant }) => ({
  backgroundColor: __variant2BackgroundColor[propVariant],
  paddingTop: 10,
  paddingRight: 22,
  paddingBottom: 10,
  paddingLeft: 22,
  borderRadius: 25,
  display: 'flex',
  justifyContent: 'space-between'
}));




const TitleTypography = styled(Typography)({
  textTransform: 'uppercase',
  fontWeight: 'bold',
  fontSize: 24,
  textAlign: 'center'
});



export interface IProps {
  returnAuthorizationId: string;
}

export const FC: React.FC<IProps> = ({ returnAuthorizationId }) => {
  const queryTuple = dataService.useQuery(returnAuthorizationId);
  const {
    modalType2IsOpen,
    setModalIsOpen
  } = modalUtil.useModalTrackers([
    'receiveItem',
    'beginTriageItem',
    'restock',
    'replaceItem',
    'returnItem',
    'creditItem',
    'scrapItem',
    'qualityControlCheck'
  ]);

  return !queryTuple.loading ? (
    <>
      <StyledPaper
        elevation={0}
        propVariant={queryTuple.data.status}>
        {queryTuple.data.status === 'preAlert' && (
          <TitleTypography>
            RMA Pre-Alert Acknowledged
          </TitleTypography>
        )}

        {queryTuple.data.status === 'preAlert' && (
          <PermissionWall.FC
            storeId={queryTuple.data.storeId}
            requiresPermissions={['tier1_saveItemReceipt']}>
            <Button
              variant="contained"
              onClick={() => setModalIsOpen('receiveItem', true)}>
              Receive Items
            </Button>
          </PermissionWall.FC>
        )}

        {queryTuple.data.status === 'itemReceived' && _.includes(queryTuple.data.enabledWorkflows, 'triage') && (
          <PermissionWall.FC
            storeId={queryTuple.data.storeId}
            requiresPermissions={['tier1_saveTriageObservation']}>
            <Button
              variant="contained"
              onClick={() => setModalIsOpen('beginTriageItem', true)}>
              Begin Triage
            </Button>
          </PermissionWall.FC>
        )}

        {queryTuple.data.status === 'observationComplete' && (
          <PermissionWall.FC
            storeId={queryTuple.data.storeId}
            requiresPermissions={['tier1_saveReturnAuthorizationTriage']}>
            <Stack
              direction="row"
              spacing={4}>
              {_.includes(queryTuple.data.enabledWorkflows, 'restock') && (
                <Button
                  variant="contained"
                  onClick={() => setModalIsOpen('restock', true)}>
                  Restock
                </Button>
              )}

              {_.includes(queryTuple.data.enabledWorkflows, 'replacement') && (
                <Button
                  variant="contained"
                  onClick={() => setModalIsOpen('replaceItem', true)}>
                  Replace
                </Button>
              )}

              {_.includes(queryTuple.data.enabledWorkflows, 'return') && (
                <Button
                  variant="contained"
                  onClick={() => setModalIsOpen('returnItem', true)}>
                  Return
                </Button>
              )}

              {_.includes(queryTuple.data.enabledWorkflows, 'credit') && (
                <Button
                  variant="contained"
                  onClick={() => setModalIsOpen('creditItem', true)}>
                  Credit
                </Button>
              )}

              {_.includes(queryTuple.data.enabledWorkflows, 'scrap') && (
                <Button
                  variant="contained"
                  onClick={() => setModalIsOpen('scrapItem', true)}>
                  Scrap
                </Button>
              )}
            </Stack>
          </PermissionWall.FC>
        )}

        {queryTuple.data.status === 'triageComplete' && (
          <Button
            variant="contained"
            onClick={() => setModalIsOpen('qualityControlCheck', true)}>
            QC Check
          </Button>
        )}

        {queryTuple.data.status === 'qualityControlComplete' && (
          <TitleTypography>
            Quality Control Complete - Pending Carton
          </TitleTypography>
        )}

        {queryTuple.data.status === 'complete' && (
          <TitleTypography>
            Complete
          </TitleTypography>
        )}
      </StyledPaper>

      {modalType2IsOpen.beginTriageItem && (
        <tier1.BeginTriageModal.FC
          returnAuthorizationId={returnAuthorizationId}
          storeId={queryTuple.data.storeId}
          onFinish={() => setModalIsOpen('beginTriageItem', false)}
          onClose={() => setModalIsOpen('beginTriageItem', false)} />
      )}

      {modalType2IsOpen.restock && (
        <tier1.RestockItemModal.FC
          returnAuthorizationId={returnAuthorizationId}
          onFinish={() => setModalIsOpen('restock', false)}
          onClose={() => setModalIsOpen('restock', false)} />
      )}

      {modalType2IsOpen.replaceItem && (
        <tier1.ReplaceItemModal.FC
          storeId={queryTuple.data.storeId}
          returnAuthorizationId={returnAuthorizationId}
          onFinish={() => setModalIsOpen('replaceItem', false)}
          onClose={() => setModalIsOpen('replaceItem', false)} />
      )}

      {modalType2IsOpen.returnItem && (
        <tier1.ReturnItemModal.FC
          returnAuthorizationId={returnAuthorizationId}
          onFinish={() => setModalIsOpen('returnItem', false)}
          onClose={() => setModalIsOpen('returnItem', false)} />
      )}

      {modalType2IsOpen.creditItem && (
        <tier1.CreditReturnAuthorizationModal.FC
          storeId={queryTuple.data.storeId}
          returnAuthorizationId={returnAuthorizationId}
          onFinish={() => setModalIsOpen('creditItem', false)}
          onClose={() => setModalIsOpen('creditItem', false)} />
      )}

      {modalType2IsOpen.scrapItem && (
        <tier1.ScrapItemModal.FC
          returnAuthorizationId={returnAuthorizationId}
          onFinish={() => setModalIsOpen('scrapItem', false)}
          onClose={() => setModalIsOpen('scrapItem', false)} />
      )}

      {modalType2IsOpen.receiveItem && (
        <tier1.ReceiveTier1ReturnAuthorizationModal.FC
          returnAuthorizationId={returnAuthorizationId}
          onFinish={() => setModalIsOpen('receiveItem', false)}
          onClose={() => setModalIsOpen('receiveItem', false)} />
      )}

      {modalType2IsOpen.qualityControlCheck && (
        <tier1.QualityControlModal.FC
          storeId={queryTuple.data.storeId}
          returnAuthorizationId={returnAuthorizationId}
          onClose={() => setModalIsOpen('qualityControlCheck', false)} />
      )}
    </>
  ) : null;
};
